import Cookie from './cookieHandler';

const config = _CONFIG_;

/**
 * @description Get nested object value from path without throwing error.
 *
 * @param {object} obj obj.
 * @param {string} path path.
 */
const deepFind = (obj, path) => {
    if (!obj) {
        return undefined;
    }

    const paths = path.split('.');
    let current = obj;
    let i;

    for (i = 0; i < paths.length; ++i) {
        if (current[paths[i]] === undefined || current[paths[i]] === null) {
            return undefined;
        }

        current = current[paths[i]];
    }

    return current;
};

const getParams = (query) => {
    if (!query) {
        return {};
    }

    return (/^[?#]/.test(query) ? query.slice(1) : query)
        .split('&')
        .reduce((params, param) => {
            const queryParams = params;
            const [key, value] = param.split('=');
            queryParams[key] = value
                ? decodeURIComponent(value.replace(/\+/g, ' '))
                : '';
            return queryParams;
        }, {});
};

/**
   * @description
   * This method is used to retrieve only the x-params necessary for purchase
   */
const getPurchaseParams = (query) => {
    let purchaseParams = undefined;

    if (query && (query.indexOf('x-show') > -1)) {
        purchaseParams = `&${query
            .slice(1)
            .split('&')
            .filter(param => param.indexOf('x-show') > -1)
            .join('&')
        }`;
    }

    return purchaseParams;
};

/**
   * @description
   * This method is used to retrieve the experiment params based on config
   */
const getExperimentParams = (experimentList, page) => {
    const parsedExperiment = {
        experimentId: '',
        experimentGroup: '',
    };

    let experiments = [];
    if (experimentList && experimentList.indexOf(',') > -1) {
        experiments = experimentList.split(',');
    } else if (experimentList) {
        experiments = [experimentList];
    }

    let secondaryExperiment = '';

    // Keep only the configured experiments for the requested page
    if (experiments.length > 1) {
        const acceptedExperiments = [];

        experiments.forEach((experiment) => {
            config.experiments_config.forEach((configuredExperiment) => {
                if ((experiment.indexOf(configuredExperiment.experimentId) > -1) &&
                    (configuredExperiment.experimentPages.indexOf(page) > -1)) {
                    acceptedExperiments.push(experiment);

                    // Save also the secondary experiment if found
                    if (configuredExperiment.experimentSecondaryPages &&
                        configuredExperiment.experimentSecondaryPages.indexOf(page) > -1) {
                        secondaryExperiment = experiment;
                    }
                }
            });
        });

        experiments = acceptedExperiments;
    }

    // Save the first experiment and the secondary one if exists
    if (experiments.length > 0) {
        const firstExperiment = experiments[0] !== secondaryExperiment ? experiments[0] : experiments[1];

        // Save the primary experiment values
        if (firstExperiment && firstExperiment.indexOf('.' > -1)) {
            parsedExperiment.experimentId = firstExperiment.split('.')[0];
            parsedExperiment.experimentGroup = firstExperiment.split('.')[1];
        }

        // Save the secondary experiment values if exists
        if (secondaryExperiment && secondaryExperiment.indexOf('.' > -1)) {
            parsedExperiment.secondaryExperimentId = secondaryExperiment.split('.')[0];
            parsedExperiment.secondaryExperimentGroup = secondaryExperiment.split('.')[1];
        }
    }

    // Add the experiment name if the experiment is configured
    if (parsedExperiment.experimentId && parsedExperiment.experimentGroup) {
        config.experiments_config.forEach((experiment) => {
            if ((parsedExperiment.experimentId.indexOf(experiment.experimentId) > -1) &&
                (experiment.experimentPages.indexOf(page) > -1)) {
                // Check valid experiment group
                if (experiment.experimentGroups.indexOf(parsedExperiment.experimentGroup) > -1) {
                    // Save the experiment name and validate the experiment
                    parsedExperiment[experiment.experimentName] = true;
                    parsedExperiment.isMixpanelValid = true;
                    parsedExperiment.isValid = true;

                // Check control group
                } else if (experiment.experimentControlGroup.indexOf(parsedExperiment.experimentGroup) > -1) {
                    // Save the experiment as valid
                    parsedExperiment.isMixpanelValid = experiment.experimentMixpanelOnControl || false;
                    parsedExperiment.isValid = true;
                }
            }
        });
    }

    // Add the experiment name of the secondary experiment if exists
    if (parsedExperiment.secondaryExperimentId && parsedExperiment.secondaryExperimentGroup) {
        config.experiments_config.forEach((experiment) => {
            if ((parsedExperiment.secondaryExperimentId.indexOf(experiment.experimentId) > -1) &&
                (experiment.experimentGroups.indexOf(parsedExperiment.secondaryExperimentGroup) > -1)) {
                // Save the experiment name
                parsedExperiment[experiment.experimentName] = true;
            }
        });
    }

    return parsedExperiment;
};

/**
   * @description
   * This method is used to retrieve the experiment values for the configured bundle
   */
const getExperimentValues = (promotions, experimentParams) => {
    const bundleId = config.promotionsBundles[0];
    const experimentValues = {
        paymentUrls: {},
        prices: {},
    };

    // If an experiment is asssigned and configured, the url, price and discount should be taken from promotions
    config.plans_experiments.forEach((experiment) => {
        if ((experimentParams.experimentId.indexOf(experiment.experimentId) > -1) &&
            (experiment.experimentGroups.indexOf(experimentParams.experimentGroup) > -1)) {
            const yearlyCode = experiment[experimentParams.experimentGroup] && experiment[experimentParams.experimentGroup].yearlyCode;

            if (yearlyCode && promotions.yearly[yearlyCode]) {
                experimentValues.paymentUrls.yearly = promotions.yearly[yearlyCode][bundleId].promotion_url;
                experimentValues.prices.yearly = promotions.yearly[yearlyCode][bundleId].total_price_string;
                experimentValues.discount = promotions.yearly[yearlyCode][bundleId].discount;
                experimentValues.isValid = true;
            }
        }
    });

    return experimentValues;
};

/**
   * @description
   * This method is used to retrieve the experiment values for all the plans bundles
   */
const getExperimentValuesPerBundle = (promotions, experimentParams) => {
    const experimentValues = {
        paymentUrls: {
            yearly: {},
        },
        prices: {
            yearly: {},
        },
    };

    // If an experiment is asssigned and configured, the url, price and discount should be taken from promotions
    config.plans_experiments.forEach((experiment) => {
        if ((experimentParams.experimentId.indexOf(experiment.experimentId) > -1) &&
            (experiment.experimentGroups.indexOf(experimentParams.experimentGroup) > -1)) {
            const yearlyCode = experiment[experimentParams.experimentGroup] && experiment[experimentParams.experimentGroup].yearlyCode;
            const targetBundles = experiment[experimentParams.experimentGroup] && experiment[experimentParams.experimentGroup].targetBundles;

            config.plansPageBundles.forEach((bundleId) => {
                if (yearlyCode && promotions.yearly[yearlyCode] && promotions.yearly[yearlyCode][bundleId] && targetBundles.indexOf(bundleId) > -1) {
                    // Save on the experiment object the urls, prices, discount and type
                    experimentValues.paymentUrls.yearly[bundleId] = promotions.yearly[yearlyCode][bundleId].promotion_url;
                    experimentValues.prices.yearly[bundleId] = promotions.yearly[yearlyCode][bundleId].total_price_string;
                    experimentValues.isValid = true;
                }
            });
        }
    });

    return experimentValues;
};

/**
   * @description
   * This method is used to retrieve the promotion values for the configured bundle
   */
const getPromotionValues = (promotions, promotionSubsource) => {
    const bundleId = config.promotionsBundles[0];
    const promotionValues = {
        paymentUrls: {},
        prices: {},
        discounts: {},
    };

    // If the promotion is valid the url, price and discount should be taken from promotions
    if (promotions.yearly && promotions.yearly[promotionSubsource]) {
        promotionValues.paymentUrls.yearly = promotions.yearly[promotionSubsource][bundleId].promotion_url;
        promotionValues.prices.yearly = promotions.yearly[promotionSubsource][bundleId].total_price_string;
        promotionValues.discounts.yearly = promotions.yearly[promotionSubsource][bundleId].discount;
        promotionValues.isValid = true;
    }

    // If the promotion is configured also for multiple years the prices should be taken from promotions
    if ((promotions.twoYears && promotions.twoYears[promotionSubsource]) &&
        (promotions.threeYears && promotions.threeYears[promotionSubsource])) {
        promotionValues.prices.twoYears = promotions.twoYears[promotionSubsource][bundleId].total_price_string;
        promotionValues.prices.threeYears = promotions.threeYears[promotionSubsource][bundleId].total_price_string;
    }

    return promotionValues;
};

/**
   * @description
   * This method is used to retrieve the offer values for the given offer
   */
const getOfferValues = (offerId) => {
    const offerValues = {};

    if (config.plansOffers.indexOf(offerId) > -1) {
        config.plans_offers_config.forEach((offer) => {
            if (offer.paramValue === offerId) {
                offerValues[offer.name] = true;
                offerValues.promotionCode = offer.promotionCode;
                offerValues.discount = offer.discount;
                offerValues.mixpanel = offer.mixpanel;
                offerValues.bundles = offer.bundles;
                offerValues.config = offer.config;
                offerValues.categories = offer.categories;
                offerValues.features = offer.features;
                offerValues.isCascadingDiscount = offer.paramValue.indexOf('cascading') > -1;
                offerValues.isValid = true;
            }
        });
    }

    return offerValues;
};

/**
   * @description
   * This method is used to retrieve the coresponding license from bundleId of the current license
   */
const getLicenseUpgrade = (licenseIntegrations, bundleId) => {
    let licenseUpgrade = false;

    if (licenseIntegrations.isDirectlyUpgradable) {
        licenseUpgrade = licenseIntegrations.isDirectlyUpgradable;
    } else {
        // Compare the components integrations with the integrations of the license
        const components = licenseIntegrations.plansBundles[bundleId] || [];
        const licenseName = licenseIntegrations.license[licenseIntegrations.licenseAppId].name;
        const licenseOeName = licenseIntegrations.license[licenseIntegrations.licenseAppId].oeName;
        const licensePrototype = licenseIntegrations.license[licenseIntegrations.licenseAppId].prototype;

        // Check if the appId of the license is amongst the current components
        if (components.indexOf(licenseIntegrations.licenseAppId) > -1) {
            licenseUpgrade = true;
        } else {
            // Compare name or prototype of the license with those of bundles components
            components.forEach((appId) => {
                const { name, oeName, prototype } = licenseIntegrations.bundlesComponents[appId];

                if (compareStrings(licenseName, name) ||
                    compareStrings(licenseOeName, oeName) ||
                    compareStrings(licensePrototype, prototype)) {
                    // Save the component integration
                    licenseUpgrade = licenseIntegrations.bundlesComponents[appId];
                }
            });
        }
    }

    return licenseUpgrade;
};

/**
   * @description
   * This method is used to retrieve the onboarding offer values for the configured bundle
   */
const getOnboardingOfferValues = (promotions, installDate, offerType) => {
    const bundleId = config.promotionsBundles[0];
    const onboardingOfferValues = {
        paymentUrls: {},
        prices: {},
    };

    const currentDate = new Date().getDate();
    const currentTimestamp = new Date().getTime();
    const offerStartTimestamp = new Date().setDate(currentDate - config.onboarding_offer_config.promotionDays);

    // Check if the install date is between the offer start timestamp and the current timestamp
    if ((installDate >= offerStartTimestamp && installDate <= currentTimestamp) || offerType === 'ipm') {
        const { yearlyCode } = config.onboarding_offer_config;

        if (yearlyCode && promotions?.yearly?.[yearlyCode]) {
            onboardingOfferValues.paymentUrls.yearly = promotions.yearly[yearlyCode][bundleId].promotion_url;
            onboardingOfferValues.prices.yearly = promotions.yearly[yearlyCode][bundleId].total_price_string;
            onboardingOfferValues.discount = promotions.yearly[yearlyCode][bundleId].discount;
            onboardingOfferValues.isValid = true;
        }

        if (offerType === 'yearly') {
            if (yearlyCode && promotions?.yearly?.[yearlyCode]) {
                onboardingOfferValues.paymentUrls.yearly = promotions.yearly[yearlyCode][bundleId].promotion_url;
                onboardingOfferValues.prices.yearly = promotions.yearly[yearlyCode][bundleId].total_price_string;
                onboardingOfferValues.discount = promotions.yearly[yearlyCode][bundleId].discount;
                onboardingOfferValues.isValid = true;
            }
        }
    }

    return onboardingOfferValues;
};

/**
   * @description
   * This method is used to retrieve the upgrade promotion values for the configured bundle
   */
const getUpgradePromotionsValues = (promotions, bundle, generalOffer, users) => {
    const bundleId = config.upgradePromotions.bundles.find(_bundle => _bundle === bundle);
    const upgradePromotionsValues = {
        isValid: false,
        discount: {},
        paymentUrls: {
            yearly: '',
            twoYears: '',
            yearly10Devices: '',
            twoYears10Devices: '',
        },
        prices: {
            yearly: '',
            twoYears: '',
            yearly10Devices: '',
            twoYears10Devices: '',
        },
    };

    const promoCode = config.upgradePromotions;
    let promotionType = 'yearly';
    promotionType += users === 10 || users === '10' ? '10Devices' : '';
    // Get the promotion codes
    if (generalOffer === 'spl_upgrade_high') {
        // Save on the onboarding offer object the urls & prices
        upgradePromotionsValues.isValid = true;
        upgradePromotionsValues.discount = promotions?.[promotionType]?.[promoCode.high]?.[bundleId]?.discount;
        if (users === 1 || users === '1') {
            upgradePromotionsValues.paymentUrls.yearly = promotions.yearly[promoCode.high][bundleId].promotion_url;
            upgradePromotionsValues.prices.yearly = promotions.yearly[promoCode.high][bundleId].total_price_string;

            upgradePromotionsValues.paymentUrls.twoYears = promotions.twoYears[promoCode.high][bundleId].promotion_url;
            upgradePromotionsValues.prices.twoYears = promotions.twoYears[promoCode.high][bundleId].total_price_string;
        } else {
            upgradePromotionsValues.paymentUrls.yearly10Devices = promotions.yearly10Devices[promoCode.high][bundleId].promotion_url;
            upgradePromotionsValues.prices.yearly10Devices = promotions.yearly10Devices[promoCode.high][bundleId].total_price_string;

            upgradePromotionsValues.paymentUrls.twoYears10Devices = promotions.twoYears10Devices[promoCode.high][bundleId].promotion_url;
            upgradePromotionsValues.prices.twoYears10Devices = promotions.twoYears10Devices[promoCode.high][bundleId].total_price_string;
        }
    } else if (generalOffer === 'spl_upgrade_low') {
        // Save on the onboarding offer object the urls & prices
        upgradePromotionsValues.isValid = true;
        upgradePromotionsValues.discount = promotions?.[promotionType]?.[promoCode.low]?.[bundleId]?.discount;
        if (users === 1 || users === '1') {
            upgradePromotionsValues.paymentUrls.yearly = promotions.yearly[promoCode.low][bundleId].promotion_url;
            upgradePromotionsValues.prices.yearly = promotions.yearly[promoCode.low][bundleId].total_price_string;

            upgradePromotionsValues.paymentUrls.twoYears = promotions.twoYears[promoCode.low][bundleId].promotion_url;
            upgradePromotionsValues.prices.twoYears = promotions.twoYears[promoCode.low][bundleId].total_price_string;
        } else {
            upgradePromotionsValues.paymentUrls.yearly10Devices = promotions.yearly10Devices[promoCode.low][bundleId].promotion_url;
            upgradePromotionsValues.prices.yearly10Devices = promotions.yearly10Devices[promoCode.low][bundleId].total_price_string;

            upgradePromotionsValues.paymentUrls.twoYears10Devices = promotions.twoYears10Devices[promoCode.low][bundleId].promotion_url;
            upgradePromotionsValues.prices.twoYears10Devices = promotions.twoYears10Devices[promoCode.low][bundleId].total_price_string;
        }
    }

    return upgradePromotionsValues;
};

/**
   * @description
   * This method is used to retrieve only the x-ipm_ parameters necessary for purchase forwarding
   */
const getIpmParams = (query) => {
    let purchaseParams = undefined;

    if (query && (query.indexOf('x-ipm_') > -1)) {
        purchaseParams = `&${query
            .slice(1)
            .split('&')
            .filter(param => param.indexOf('x-ipm_') > -1)
            .join('&')
        }`;
    }

    return purchaseParams;
};

/**
   * @description
   * This method is used to compare two strings
   */
const compareStrings = (stringOne, stringTwo) => {
    if (stringOne && stringTwo) {
        const a = stringOne.toLowerCase();
        const b = stringTwo.toLowerCase();

        if (a.length < b.length) {
            return b.indexOf(a) > -1;
        }

        return a.indexOf(b) > -1;
    }

    return false;
};

/**
   * @description
   * This method is used to retrieve the authentication token from cookie
   */
const getTokenFromCookies = () => {
    return Cookie.get(config.cwDashboardCookie);
};

/**
 * @description
 * Applies the first matching rule from a list of rule objects.
 * Returns The payload or value generated by the first matching rule's build function.
 */

const getMixpanelTrackingPayload = (rules, context) =>
    rules.find(rule => rule.match(context)).build(context);

export {
    deepFind,
    getParams,
    getExperimentParams,
    getExperimentValues,
    getExperimentValuesPerBundle,
    getPromotionValues,
    getOfferValues,
    getLicenseUpgrade,
    compareStrings,
    getPurchaseParams,
    getOnboardingOfferValues,
    getIpmParams,
    getTokenFromCookies,
    getUpgradePromotionsValues,
    getMixpanelTrackingPayload,
};
